import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoIosArrowDown } from 'react-icons/io';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { postFetcher } from '../../../api/fetchers';
import { endpoints } from '../../../api/endpoints';
import { ToastContainer, toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';


interface Order {
    id: string;
    orderNumber: string;
    totalAmount: string;
    deliveryStatus: number;
    deliveryDate: string;
    createdAt: string;
    productInfo: {
        productId: string;
        productName: string;
        productPrice: number;
        quantity: number;
        images: string;
    };
    supplierInfo: {
        supplierId: string;
        supplierFullName: string;
        supplierMarketPlaceName: string;
    };
    customerName: string;
    customerPhone: string;
    deliveryAddress: string;
}

interface LocationState {
    order: Order;
    date: string;
    totalPrice: string;
    orderCount: number;
    previousPath?: string;
}

const ProductReturn: React.FC = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState;
    const previousPath = state?.previousPath || '/profile/orders';
    const order = state?.order;
    const date = state?.date;
    const totalPrice = state?.totalPrice;
    const orderCount = state?.orderCount;
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isSecondModalOpen, setIsSecondModalOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);


    //Go Back
    const handleGoBack = () => {
        navigate(previousPath, { state: { groupOrders: [order], date, totalPrice, orderCount } });
    };

    //Confirm Return
    /*    const handleConfirmReturn = () => {
            setIsModalOpen(false);
        };*/


    //Close Second Modal
    const handleSecondModalClose = () => {
        setIsSecondModalOpen(false);
    };

    //Close Dropdown
    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleDropdownSelect = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void, quantity: number) => {
        setFieldValue('returnQuantity', quantity);
        setIsDropdownOpen(false);
    };

    //Generate Return Code
    const generateReturnCode = (): string => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let returnCode = '';
        for (let i = 0; i < 8; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            returnCode += characters[randomIndex];
        }
        return returnCode;
    };

    const validationSchema = Yup.object({
        returnReason: Yup.string().required('Reason for return required'),
        returnDescription: Yup.string().required('Return explanation required'),
        returnQuantity: Yup.number().required('Amount to be refunded required')
    });

    const Modal: React.FC<{
        isOpen: boolean;
        onClose: () => void;
        title: string;
        children: React.ReactNode
    }> = ({ isOpen, onClose, title, children }) => {
        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                <div className="bg-white rounded-lg w-11/12 md:w-3/4 lg:w-1/2">
                    <div className="p-4 border-b flex justify-between items-center">
                        <h2 className="text-xl font-bold">{title}</h2>
                        <button className="text-gray-700" onClick={onClose}>X</button>
                    </div>
                    <div className="p-4">
                        {children}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={`p-4 pb-14 sm:p-8 mx-2 sm:mx-4 shadow-lg rounded-lg bg-white overflow-y-auto max-h-[calc(100vh-11rem)] h-full ${isModalOpen || isSecondModalOpen ? 'overflow-hidden' : ''}`}>
            <button onClick={handleGoBack} className="text-primary-color font-medium text-sm mb-4 inline-block">
                {`< ${t('productReturn', 'back')}`}
            </button>

            <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-gray-300">
                <div className="flex flex-col sm:flex-row justify-around items-center text-center gap-4">
                    <h2 className="text-xl font-bold">{t('productReturn', 'title')}</h2>
                    <div className="flex flex-col">
                        <span className="font-semibold">{t('productReturn', 'orderDate')}</span>
                        <span>{date}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="font-semibold">{t('productReturn', 'orderNumber')}</span>
                        <span>{order.orderNumber}</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="font-semibold">{t('productReturn', 'orderSummary')}</span>
                        <span className="text-green-600">{orderCount} {t('productReturn', 'productDelivered')}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <img src={(order.productInfo.images)[0]} alt={order.productInfo.productName}
                    className="w-32 h-auto object-contain" />
            </div>

            <Formik
                initialValues={{ returnReason: '', returnDescription: '', returnQuantity: 1 }}
                validationSchema={validationSchema}
                onSubmit={async (values) => {

                    async function postReturnRequest(values: any) {
                        try {
                            const response = await postFetcher(endpoints.orders.refundRequest, {
                                arg: {
                                    orderId: order.id,
                                    reason: values.returnReason,
                                    description: values.returnDescription,
                                }
                            });

                            if (response.result) {
                                setIsSecondModalOpen(true);
                                toast.success( t('toastMessage', 'rate15'));
                            } else {
                                if (response.message = 'Refund request already exists for this order') {
                                    setIsModalOpen(false);
                                    setIsSecondModalOpen(false);
                                    return toast.error(t('toastMessage', 'rate16'));
                                } else {
                                    toast.error(t('toastMessage', 'rate17'));
                                    console.log('ERROR!');
                                }

                            }
                        } catch (error) {
                            console.log(error)
                            toast.error(t('toastMessage', 'rate17'));
                        }

                    }
                    postReturnRequest(values);
                }}

            >
                {({ setFieldValue }) => (
                    <Form className="space-y-6">
                        <div className="w-full sm:w-1/2 md:w-1/3">
                            <label className="block text-gray-700 pt-3">{t('productReturn', 'pReturn')}</label>
                            <div
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary-color focus:border-primary-color bg-gray-100 px-4 py-2">
                                {order.productInfo.productName}
                            </div>
                        </div>

                        <div ref={dropdownRef} className="relative w-full sm:w-1/2 md:w-1/3">
                            <label className="block text-gray-700">{t('productReturn', 'pQuantity')}</label>
                            <button
                                type="button"
                                className={`inline-flex justify-between w-full pl-4 p-2 mt-1 bg-transparent border border-gray-300 rounded-md focus:outline-none shadow-sm sm:text-sm`}
                                id="menu-button"
                                aria-expanded={isDropdownOpen}
                                aria-haspopup="true"
                                onClick={toggleDropdown}
                            >
                                <div className="flex items-center gap-2">
                                    <p className="font-semibold text-primary-color">{t('productReturn', 'pQuantity')}:</p>
                                    <Field name="returnQuantity">
                                        {({ field }: any) => (
                                            <span {...field}>{field.value}</span>
                                        )}
                                    </Field>
                                </div>
                                <IoIosArrowDown className="ml-2 h-5 w-5 text-gray-400" />
                            </button>
                            {isDropdownOpen && (
                                <div
                                    className="absolute z-10 mt-2 w-full max-h-32 overflow-y-auto origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    role="menu" aria-orientation="vertical" aria-labelledby="menu-button">
                                    <div className="py-1" role="none">
                                        {Array.from({ length: order.productInfo.quantity }, (_, i) => i + 1).map((quantity) => (
                                            <button
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary-color w-full text-left"
                                                role="menuitem"
                                                key={quantity}
                                                onClick={() => handleDropdownSelect(setFieldValue, quantity)}
                                            >
                                                {quantity}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <ErrorMessage name="returnQuantity" component="div" className="text-red-500 text-sm" />
                        </div>

                        <div className="w-full sm:w-3/5">
                            <label className="block text-gray-700">{t('productReturn', 'pReason')}</label>
                            <Field
                                name="returnReason"
                                type="text"
                                className="mt-1 block w-full p-4 border border-gray-300 rounded-md shadow-lg focus:ring-primary-color focus:border-primary-color"
                                placeholder={t('productReturn', 'pReason')}
                            />
                            <ErrorMessage name="returnReason" component="div" className="text-red-500 text-sm" />
                        </div>

                        <div className="w-full sm:w-3/5">
                            <label className="block text-gray-700">{t('productReturn', 'pReason2')}</label>
                            <Field
                                as="textarea"
                                name="returnDescription"
                                className="mt-1 block w-full p-4 border border-gray-300 rounded-md shadow-md focus:ring-primary-color focus:border-primary-color"
                                placeholder={t('productReturn', 'pReason2')}
                                rows={6}
                            />
                            <ErrorMessage name="returnDescription" component="div" className="text-red-500 text-sm" />
                        </div>

                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-primary-color text-white px-4 py-2 rounded-md w-full sm:w-auto">
                            {t('productReturn', 'button')}
                        </button>
                    </Form>
                )}
            </Formik>


            {/* <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="">
                <div className="text-center p-4 max-w-sm mx-auto">
                    <p className="mb-4">
                        İade edilecek ürün kampanya kapsamındadır. İadeniz, kampanyalı ürün
                        olduğundan kampanya koşullarına uygun olmalıdır.
                    </p>
                    <div className="flex justify-center flex-col space-y-6">
                        <button
                            type='submit'
                            onClick={handleConfirmReturn}
                            className="bg-green-600 text-white p-4 rounded w-full md:w-80"
                        >
                            Devam Et
                        </button>
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="bg-red-600 text-white p-4 rounded md:w-80"
                        >
                            Vazgeç
                        </button>
                    </div>
                </div>
            </Modal> */}

            <Modal isOpen={isSecondModalOpen} onClose={handleSecondModalClose} title="">
                <div className="text-center p-4 max-w-sm mx-auto">
                    <p className="mb-4">İade edilecek tutar: {totalPrice}</p>
                    <p className="mb-4">İade gönderim kodunuz:</p>
                    <p className="text-2xl font-bold text-red-600">{generateReturnCode()}</p>
                </div>
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </div>
    );
};

export default ProductReturn;
